import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Spinner } from './helpers/Spinner';
import { OmniumTable } from './tables/OmniumTable';
import { RelatedEvents } from './helpers/RelatedEvents';
import { ResultsTable } from './tables/ResultsTable';
import { parseBlocks } from '../utils/parseBlocks';
import { MultipleRidersTable } from './tables/MultipleRidersTable';
import { SprintTable } from './tables/SprintTable';
import { PursuitTable } from './tables/PursuitTable';
import { TeamPursuitSprintTable } from './tables/TeamPursuitSprintTable';
import { PointsRaceTable } from './tables/PointsRaceTable';
import { MadisonTable } from './tables/MadisonTable';
import { NoDisciplineData } from './helpers/NoDisciplineData';

// Helper type definitions
interface Discipline {
    name: string;
    sheetName?: string;
    rowRange: [number, number];
    columns: string[];
    relatedEvents?: string[];
    displayName?: string;
    boldRows?: number[];
}

interface EventDay {
    day: number;
    date: string;
    events: Discipline[];
}

interface EventData {
    name: string;
    googleSheetsId: string;
    disciplines: EventDay[];
}

export const DisciplineLive: FC = () => {
    const { eventName, disciplineName } = useParams<{ eventName: string; disciplineName: string }>();
    const navigate = useNavigate();

    // State
    const [eventDisplayName, setEventDisplayName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [data, setData] = useState<any[][]>([]);
    const [sheetName, setSheetName] = useState('');
    const [relatedEvents, setRelatedEvents] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSheetLoading, setIsSheetLoading] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Load event metadata
    useEffect(() => {
        if (!eventName || !disciplineName) return;

        setIsLoading(true);

        fetch(`/api/events/${eventName}`)
            .then((res) => res.json())
            .then((jsonData: EventData) => {
                setEventDisplayName(jsonData.name);

                const discipline = jsonData.disciplines
                    .flatMap((day) => day.events)
                    .find(
                        (d) =>
                            d.name.replace(/\s+/g, '-').replace(/\//g, '-').toLowerCase() ===
                            disciplineName.toLowerCase(),
                    );

                if (discipline) {
                    setSheetName(discipline.sheetName || '');
                    setRelatedEvents(discipline.relatedEvents || []);
                    setDisplayName(discipline.displayName || discipline.name);
                    const selectedIndex = discipline.relatedEvents?.findIndex(
                        (item) => item === discipline.displayName || item === discipline.name,
                    );
                    setSelectedIndex(selectedIndex || 0);
                }
            })
            .finally(() => setIsLoading(false))
            .catch((err) => console.error('❌ Error loading event JSON:', err));
    }, [eventName, disciplineName]);

    // Load Google Sheets data
    useEffect(() => {
        if (!eventName || !disciplineName) return;

        setIsSheetLoading(true);

        const fetchData = () => {
            fetch(`/api/sheets/${eventName}/${disciplineName}`)
                .then((res) => res.json())
                .then((jsonData: { values: any[][] }) => {
                    if (jsonData.values) {
                        setData(jsonData.values);
                    }
                })
                .finally(() => setIsSheetLoading(false))
                .catch((err) => console.error('❌ Error loading Google Sheets data:', err));
        };

        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, [eventName, disciplineName]);

    // 🔝 IDENTIFIKACE DISCIPLÍN PŘESUNUTÁ SEM (Před useMemo kvůli pořadí inicializace)
    // 🎯 UPRAVENO: Celkové Omnium teď vyžaduje jak Om. Points, tak sloupec Scratch nebo Elim
    const isOmnium =
        data?.[0]?.some((h) => h.trim().toLowerCase() === 'om. points') &&
        (data?.[0]?.some((h) => h.trim().toLowerCase() === 'scratch') || data?.[0]?.some((h) => h.trim().toLowerCase() === 'elim'));
    const isSprint = data?.[0]?.some((h) => h.trim().toLowerCase() === 'decider');

   
    
    const isMadison = sheetName?.toLowerCase()?.includes('madison');
    // 🎯 UPRAVENO: Bodovačka teď odchytí i Tempo Race a dílčí Omnium - Points Race!
    const isPointsRace = !isOmnium && (
        disciplineName?.toLowerCase().includes('points-race') || 
        disciplineName?.toLowerCase().includes('points race') || 
        disciplineName?.toLowerCase().includes('tempo-race') || 
        disciplineName?.toLowerCase().includes('tempo race') || 
        sheetName?.toLowerCase().includes('points race') ||
        sheetName?.toLowerCase().includes('tempo race')
    );
    const isTeamEvent = sheetName?.toLowerCase()?.includes('team');
const urlName = disciplineName?.toLowerCase() || '';
    const isTeamPursuitOrSprint = urlName.includes('team-pursuit') || urlName.includes('team pursuit') || 
                                  urlName.includes('team-sprint') || urlName.includes('team sprint');    
     const isPursuit = disciplineName?.toLowerCase().includes('pursuit') && !isTeamEvent;

    // 🛠️ PARSER DATA (Teď už bezpečně ví, co je isTeamEvent)
    const parsed = useMemo(() => {
        if (!data || data.length === 0) return [];

        // POUZE PRO TEAM SPRINT: Vyčistíme data od prázdných řádků dřív, než je dostane parser
        if (isTeamEvent) {
            const cleanedData = data.filter((row) => {
                if (!row || row.length === 0) return false;
                
                // Zkontrolujeme, zda řádek obsahuje aspoň nějaký text/data
                const hasContent = row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '');
                
                // Pokud je řádek úplně prázdný (mezera mezi 3. a 4. jezdcem), zahodíme ho
                return hasContent;
            });
            
            return parseBlocks(cleanedData);
        }

        // Všechny ostatní disciplíny (včetně Madisonu!) jedou beze změny postaru
        return parseBlocks(data);
    }, [data, isTeamEvent]);

    if (isLoading || isSheetLoading) {
        return <Spinner />;
    }

    return (
        <>
            <Box sx={{ width: isMobile ? '90%' : '85%', mx: 'auto', mt: 4 }}>
                {/* 🔙 Back Button */}
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/event/${eventName}`)} sx={{ mb: 2 }}>
                    Back to {eventDisplayName || eventName?.replace(/-/g, ' ')}
                </Button>

                {/* 🏁 Discipline Header */}
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
                    <Typography variant="h4" fontWeight={700}>
                        {displayName}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
                        Results
                    </Typography>
                </Paper>

                {/* 🔀 Related Events */}
                {relatedEvents.length > 0 && (
                    <RelatedEvents
                        relatedEvents={relatedEvents}
                        selectedIndex={selectedIndex}
                        setSelectedIndex={setSelectedIndex}
                        eventName={eventName || ''}
                    />
                )}

               

               {/* 📊 Data Table / Component */}
                {data.length === 0 && <NoDisciplineData />}

                {/* TADY ZAČÍNÁ NAŠE DOPRAVNÍ ZNAČENÍ: */}
                {isTeamPursuitOrSprint ? (
                    // 1. Týmová Stíhačka a Týmový Sprint
                    <TeamPursuitSprintTable values={data} />
                ) : isPursuit ? (
                    // 2. Individuální Stíhačka
                    <PursuitTable values={data} />
                ) : isMadison ? (
                    // 3. 🎯 NOVÉ: Když je to Madison, pošli data do naší nové MadisonTable!
                    parsed?.map((block, index) => {
                        if (block.data.length === 1) return null;
                        return <MadisonTable values={block.data} title={block.tableTitle} key={index} />;
                    })
                ) : isPointsRace ? (
                    // 4. 🎯 NOVÉ: Když je to Bodovačka, pošli data do naší nové PointsRaceTable!
                    parsed?.map((block, index) => {
                        if (block.data.length === 1) return null;
                        return <PointsRaceTable values={block.data} title={block.tableTitle} key={index} />;
                    })
                ) : (
                    // 5. Všechno ostatní (Scratch, Keirin, Omnium...) jde starou cestou
                    parsed?.map((block, index) => {
                        if (block.data.length === 1) return null;

                        if (isOmnium) {
                            return <OmniumTable values={block.data} key={index} />;
                        } else if (isTeamEvent) {
                            return (
                                <MultipleRidersTable 
                                    values={block.data} 
                                    title={block.tableTitle} 
                                    key={index}
                                    isTeam={isTeamEvent} 
                                />
                            );
                        } else if (isSprint) {
                            return <SprintTable values={block.data} title={block.tableTitle} key={index} />;
                        } else {
                            return <ResultsTable values={block.data} title={block.tableTitle} key={index} />;
                        }
                    })
                )}
            </Box>
    
        </>
    );
};